<template>
  <z-dialog
    :model-value="modelValue"
    title="发起群聊"
    width="760px"
    append-to-body
    align-center
    destroy-on-close
    :show-close="false"
    class="group-chat-dialog"
    header-class="group-chat-dialog__header--hidden"
    body-class="group-chat-dialog__body"
    @update:model-value="emit('update:modelValue', $event)"
  >
    <div v-loading="loading" class="group-chat">
      <section class="group-chat__left">
        <z-input
          v-model="keyword"
          clearable
          placeholder="搜索"
          :prefix-icon="Search"
        />

        <div class="group-chat__tree">
          <z-tree
            ref="treeRef"
            :data="treeData"
            node-key="id"
            show-checkbox
            :default-expanded-keys="expandedKeys"
            :expand-on-click-node="false"
            :check-on-click-node="true"
            :filter-node-method="filterNode"
            :props="treeNodeProps"
            @check="syncSelected"
          >
            <template #default="{ data }">
              <span class="group-chat__node">
                <z-icon class="group-chat__node-icon" :class="`is-${data.kind}`">
                  <component :is="nodeIcon(data.kind)" />
                </z-icon>
                <span>{{ data.label }}</span>
              </span>
            </template>
          </z-tree>
          <z-empty v-if="!treeData.length && !loading" description="暂无组织数据" />
        </div>
      </section>

      <section class="group-chat__right">
        <header class="group-chat__right-head">
          <h3 class="group-chat__title">发起群聊</h3>
          <span class="group-chat__count">已选择{{ selected.length }}个联系人</span>
        </header>
        <div class="group-chat__picked">
          <div
            v-for="member in selected"
            :key="member.id"
            class="group-chat__row"
          >
            <z-avatar
              :size="32"
              shape="square"
              :icon="memberIcon(member.kind)"
            />
            <span class="group-chat__row-name">{{ member.name }}</span>
            <z-button
              :icon="Close"
              link
              class="group-chat__row-remove"
              aria-label="移除"
              @click="removeMember(member.id)"
            />
          </div>
        </div>
        <div class="group-chat__actions">
          <z-button
            type="primary"
            :disabled="!selected.length"
            @click="confirm"
          >
            完成
          </z-button>
          <z-button link @click="emit('update:modelValue', false)">
            取消
          </z-button>
        </div>
      </section>
    </div>
  </z-dialog>
</template>

<script setup lang="ts">
import { computed, nextTick, ref, watch } from 'vue'
import {
  Close,
  Folder,
  OfficeBuilding,
  Search,
  User,
} from '@zoryn/icons'
import ZAvatar from '@zoryn/components/avatar'
import ZButton from '@zoryn/components/button'
import ZDialog from '@zoryn/components/dialog'
import ZEmpty from '@zoryn/components/empty'
import ZIcon from '@zoryn/components/icon'
import ZInput from '@zoryn/components/input'
import ZTree from '@zoryn/components/tree'
import type { TreeInstance } from '@zoryn/components/tree'
import {
  MOCK_GROUP_DEPTS,
  MOCK_GROUP_PERSONS,
  collectExpandedOrgKeys,
  wrapCompanyTree,
  type GroupChatMember,
  type GroupChatNode,
  type GroupChatNodeKind,
} from '../utils/group-chat'
import '@zoryn/components/avatar/style'
import '@zoryn/components/button/style'
import '@zoryn/components/dialog/style'
import '@zoryn/components/empty/style'
import '@zoryn/components/icon/style'
import '@zoryn/components/input/style'
import '@zoryn/components/tree/style'

const props = defineProps<{
  modelValue: boolean
  loadOrg?: () => Promise<GroupChatNode[]>
}>()

const emit = defineEmits<{
  'update:modelValue': [value: boolean]
  confirm: [members: GroupChatMember[]]
}>()

const loading = ref(false)
const keyword = ref('')
const treeData = ref<GroupChatNode[]>([])
const selected = ref<GroupChatMember[]>([])
const treeRef = ref<TreeInstance>()

const treeNodeProps = {
  label: 'label',
  children: 'children',
  disabled: (data: GroupChatNode, node: { level: number }) =>
    data.kind === 'company' && node.level === 1,
}

const expandedKeys = computed(() => collectExpandedOrgKeys(treeData.value))

watch(
  () => props.modelValue,
  async (visible) => {
    if (!visible) return
    keyword.value = ''
    selected.value = []
    await loadOrg()
    await nextTick()
    treeRef.value?.setCheckedKeys([])
    treeRef.value?.filter('')
  }
)

watch(keyword, (value) => {
  treeRef.value?.filter(value.trim())
})

function nodeIcon(kind: GroupChatNodeKind) {
  if (kind === 'company') return OfficeBuilding
  if (kind === 'user') return User
  return Folder
}

function memberIcon(kind: GroupChatNodeKind) {
  if (kind === 'company' || kind === 'dept') return Folder
  return User
}

function filterNode(value: string, data: GroupChatNode) {
  if (!value) return true
  return data.label.includes(value)
}

async function loadOrg() {
  if (props.loadOrg) {
    loading.value = true
    try {
      treeData.value = (await props.loadOrg()) ?? []
    } catch {
      treeData.value = wrapCompanyTree(
        MOCK_GROUP_DEPTS,
        MOCK_GROUP_PERSONS,
        '崇达技术'
      )
    } finally {
      loading.value = false
    }
    return
  }
  treeData.value = wrapCompanyTree(
    MOCK_GROUP_DEPTS,
    MOCK_GROUP_PERSONS,
    '崇达技术'
  )
}

function syncSelected() {
  const nodes = (treeRef.value?.getCheckedNodes(true, false) ?? []) as GroupChatNode[]
  selected.value = nodes
    .filter((node) => node.kind === 'user')
    .map((node) => ({ id: node.id, name: node.label, kind: node.kind }))
}

function removeMember(id: string) {
  treeRef.value?.setChecked(id, false, true)
  syncSelected()
}

function confirm() {
  if (!selected.value.length) return
  emit('confirm', selected.value.map((item) => ({ ...item })))
  emit('update:modelValue', false)
}
</script>
